import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { ElementAddress, MediaText } from 'editor/src/store/design/types';
import {
  FrameLayout,
  getTextFlowChains,
  getTextFlowChainText,
  splitTextOverFrames,
  TextFlowMember,
} from 'editor/src/store/design/util/textFlow';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { OVERFLOW_LINE_TOLERANCE } from 'editor/src/fabric/FabricPathText';

import { getRenderedTextObject } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

import { canJoinTextFlow } from './linkTextFramesOperation';

// Laid out as the canvas lays the frame out (the same FabricPathText, in mm).
const layoutInFrame =
  (element: MediaText) =>
  (text: string): FrameLayout => {
    const object = getRenderedTextObject({ ...element, extra: { ...element.extra, text } });
    return {
      graphemes: object._text,
      lines: object._textLines,
      lineHeights: object._textLines.map((_, i) => object.getHeightOfLine(i)),
      lineHeightFactor: object.lineHeight,
      tolerance: object.getHeightOfLine() * OVERFLOW_LINE_TOLERANCE,
    };
  };

// Chains that needed no change, by their first frame: skipped until a frame or the fonts change, so
// a design change elsewhere does not lay every chain out again.
const settledChains = new WeakMap<MediaText, { elements: MediaText[]; fonts: unknown }>();

const isSettled = (members: TextFlowMember[], fonts: unknown) => {
  const settled = settledChains.get(members[0].element);
  if (!settled || settled.fonts !== fonts) {
    return false;
  }
  return (
    settled.elements.length === members.length &&
    settled.elements.every((element, index) => element === members[index].element)
  );
};

const unlink = ({ element, address }: TextFlowMember, keepFieldKey = true) => {
  const { textFlow, ...extra } = element.extra;
  return { address, update: { extra: keepFieldKey ? extra : { ...extra, fieldKey: undefined } } as Partial<MediaText> };
};

/**
 * Pours the text of every chain of linked frames over its frames again. It writes no undo step,
 * so undoing the change that set it off restores the frames. A chain waits while one of its
 * frames is typed in (fabric holds that text until editing ends) and until its fonts load. A frame
 * that can no longer be linked (curved, autofit, a protected font, a layer something can hide) leaves
 * the chain with the text it shows, so the rest keeps flowing.
 */
const flowLinkedTextsOperation = () => (dispatch: ThunkDispatch, getState: () => RootState) => {
  const state = getState();
  const chains = getTextFlowChains(state.design.designData);
  if (!chains.length) {
    return;
  }
  const editedUuids = state.editor.textEditMode ? state.editor.selectedElementUuids : [];
  const fonts = state.fonts.availableFonts;
  const updates: Array<{ address: ElementAddress; update: Partial<MediaText> }> = [];

  chains.forEach((chain) => {
    // a copied frame carries its original's link (and sorts after it), and a frame that can no longer
    // take a fixed frame cannot be poured into: both leave the chain
    const indexes = new Set<number>();
    const members = chain.filter((member) => {
      const index = member.element.extra.textFlow?.index ?? 0;
      if (
        indexes.has(index) ||
        !member.element.extra.fixedFrame ||
        !canJoinTextFlow(state, member.element, member.address.spreadIndex)
      ) {
        return false;
      }
      indexes.add(index);
      return true;
    });
    // the host fills a chain through its first frame's key (linkTextFramesOperation): a first frame
    // that leaves hands it to the new first one
    const { fieldKey } = chain[0].element.extra;
    const handsKeyOn = !!fieldKey && members[0] !== chain[0] && !!members[0];
    chain.forEach((member) => !members.includes(member) && updates.push(unlink(member, !handsKeyOn)));
    if (handsKeyOn) {
      const [first] = members;
      members[0] = { ...first, element: { ...first.element, extra: { ...first.element.extra, fieldKey } } };
    }
    if (members.length < 2) {
      members.forEach((member) => updates.push(unlink(member)));
      return;
    }
    if (handsKeyOn) {
      // written alone, and poured on the next flow, which this change sets off
      updates.push({ address: members[0].address, update: { extra: members[0].element.extra } });
      return;
    }
    if (
      isSettled(members, fonts) ||
      members.some(({ element }) => editedUuids.includes(element.uuid)) ||
      !members.every(({ element }) => isFontFileLoaded(element.extra.fontFamily))
    ) {
      return;
    }

    const id = members[0].element.extra.textFlow?.id ?? '';
    const slices = splitTextOverFrames(
      getTextFlowChainText(members),
      members.map(({ element }) => ({ height: element.height, layout: layoutInFrame(element) })),
    );
    const updateCount = updates.length;
    members.forEach(({ element, address }, index) => {
      const { text } = slices[index];
      const joint = index > 0 ? slices[index].joint : undefined;
      const flow = element.extra.textFlow;
      if (text === element.extra.text && joint === flow?.joint && index === flow?.index) {
        return;
      }
      const update: Partial<MediaText> = { extra: { ...element.extra, text, textFlow: { id, index, joint } } };
      if (text !== element.extra.text && element.extra.fontFamily.startsWith('custom-')) {
        update.path = undefined;
      }
      updates.push({ address, update });
    });
    if (updates.length === updateCount) {
      settledChains.set(members[0].element, { elements: members.map(({ element }) => element), fonts });
    }
  });

  if (updates.length) {
    batch(() => {
      updates.forEach(({ address, update }) =>
        dispatch(updateMediaElementAction({ elementAddress: address, elementUpdate: update })),
      );
    });
  }
};

export default flowLinkedTextsOperation;
